import { useState, useEffect } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Bell, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { usePushNotifications } from "@/hooks/usePushNotifications";

export default function PushNotificationPrompt() {
  const [dismissed, setDismissed] = useState(false);
  const { isSupported, isSubscribed, permission, loading, subscribe } = usePushNotifications();

  useEffect(() => {
    const wasDismissed = sessionStorage.getItem("pushPromptDismissed");
    if (wasDismissed) setDismissed(true);
  }, []);

  const dismiss = () => {
    setDismissed(true);
    sessionStorage.setItem("pushPromptDismissed", "true");
  };

  // Don't show if: not supported, already subscribed, permission denied, or dismissed
  if (!isSupported || isSubscribed || permission === "denied" || dismissed) {
    return null;
  }

  const handleEnable = async () => {
    const success = await subscribe();
    if (success) {
      toast.success("Push notifications enabled! You'll be alerted when tool thieves are active in your area.");
      setDismissed(true);
    } else if (Notification.permission === "denied") {
      toast.error("Notifications blocked. You can enable them in your browser settings.");
      dismiss();
    }
  };

  return (
    <Alert className="mb-4 md:mb-6 border-yellow-500/50 bg-yellow-500/10" data-testid="push-notification-prompt">
      <Bell className="h-4 w-4 text-yellow-400" />
      <AlertDescription className="flex items-center justify-between gap-2">
        <span className="text-sm md:text-base">
          <strong>Enable push notifications</strong> — Get instant alerts when your tools are scanned and when tool thieves are active in your area.
        </span>
        <div className="flex items-center gap-1 shrink-0">
          <Button 
            size="sm" 
            onClick={handleEnable} 
            disabled={loading}
            className="h-7 px-3 text-xs"
            data-testid="enable-push-btn"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Enable"}
          </Button>
          <Button variant="ghost" size="sm" onClick={dismiss} className="h-6 px-2">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
